import { Controller } from "@hotwired/stimulus"

// § 3.5 Tier 2 Step 15 — ACC Docs file picker.
//
// The browser never receives an APS bearer token — the controller hits
// /projects/:id/bim/acc_browse.json (server-side proxy) for every
// folder navigation. Picking a file POSTs to /projects/:id/bim with
// the resolved item URN; the server creates the BimModel row +
// enqueues the translation job.
//
// Tree-style navigation: each click into a folder pushes onto the
// breadcrumb stack; clicking a breadcrumb entry pops back. Files
// rendered inside a folder are extension-filtered server-side.
export default class extends Controller {
  static targets = ["modal", "backdrop", "content", "breadcrumb", "error"]
  static values = {
    browseUrl: String,
    createUrl: String,
    csrf:      String
  }

  connect() {
    this._stack = []
    this._onKeydown = (event) => {
      if (event.key === "Escape" && !this.modalTarget?.classList.contains("hidden")) {
        this.close()
      }
    }
    document.addEventListener("keydown", this._onKeydown)

    if (new URLSearchParams(window.location.search).get("open_picker") === "1") {
      // Defer one tick so the index controller has wired up first.
      setTimeout(() => this.open(), 50)
    }
  }

  disconnect() {
    if (this._onKeydown) {
      document.removeEventListener("keydown", this._onKeydown)
      this._onKeydown = null
    }
  }

  open() {
    if (!this.hasModalTarget) return
    this.modalTarget.classList.remove("hidden")
    this.backdropTarget.classList.remove("hidden")
    this._stack = []
    this._navigate(null, "Top folders")
  }

  close(event) {
    if (event && event.target !== event.currentTarget) {
      // Click bubbled from inside the modal panel; ignore.
      return
    }
    this.modalTarget.classList.add("hidden")
    this.backdropTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // Navigate to `folderId`. nil = top folders. Updates breadcrumb +
  // pushes onto the stack so the user can navigate back.
  async _navigate(folderId, label) {
    this._renderLoading()
    this._clearError()

    const url = new URL(this.browseUrlValue, window.location.origin)
    if (folderId) url.searchParams.set("folder_id", folderId)

    try {
      const response = await fetch(url.toString(), {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok || !json.ok) {
        this._renderError(json?.message || `Couldn't load ACC contents (HTTP ${response.status}).`)
        return
      }

      // Update the navigation stack only when the call succeeded so
      // the breadcrumb doesn't show a folder we never landed in.
      if (folderId === null) {
        this._stack = [{ id: null, label: label || "Top folders" }]
      } else {
        // Don't push duplicates if the user clicks the same folder
        // twice (e.g. mid-load misclick).
        if (this._stack[this._stack.length - 1]?.id !== folderId) {
          this._stack.push({ id: folderId, label: label || "Folder" })
        }
      }

      this._renderBreadcrumb()
      this._renderContents(json.folders || [], json.files || [])
    } catch (err) {
      this._renderError(`Couldn't reach ACC: ${err?.message || err}`)
    }
  }

  _navigateToCrumb(event) {
    const idx = parseInt(event.currentTarget.dataset.crumbIndex, 10)
    if (Number.isNaN(idx) || idx < 0 || idx >= this._stack.length) return
    const target = this._stack[idx]
    // Trim the stack to the clicked depth (the navigate call rebuilds
    // it on success).
    this._stack = this._stack.slice(0, idx)
    this._navigate(target.id, target.label)
  }

  // Pick a file row → POST to the create endpoint. Server returns
  // { ok, message, id } and the page reloads to land on the new model.
  async _pickFile(event) {
    const tile = event.currentTarget
    const item = JSON.parse(tile.dataset.item || "{}")
    if (!item || !item.latest_version_urn) {
      this._renderError("That file has no translatable version yet.")
      return
    }

    this._clearError()
    tile.classList.add("opacity-60", "pointer-events-none")

    const folder = this._stack[this._stack.length - 1]?.id || ""
    const formData = new FormData()
    formData.append("bim_model[acc_item_id]", item.id || "")
    formData.append("bim_model[acc_version_id]", item.latest_version_id || "")
    formData.append("bim_model[acc_version_urn]", item.latest_version_urn || "")
    formData.append("bim_model[acc_folder_id]", folder)
    formData.append("bim_model[source_filename]", item.name || "")
    formData.append("bim_model[source_size_bytes]", String(item.size_bytes || 0))

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        body: formData,
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfValue
        },
        credentials: "same-origin"
      })
      const json = await response.json().catch(() => ({}))
      if (!response.ok || !json.ok) {
        tile.classList.remove("opacity-60", "pointer-events-none")
        this._renderError(json?.message || `Couldn't add that model (HTTP ${response.status}).`)
        return
      }

      // Land on the new model row.
      window.location.href = json.id
        ? `${window.location.pathname}/${json.id}`
        : window.location.pathname
    } catch (err) {
      tile.classList.remove("opacity-60", "pointer-events-none")
      this._renderError(`Couldn't reach ConsLog: ${err?.message || err}`)
    }
  }

  _renderLoading() {
    if (!this.hasContentTarget) return
    this.contentTarget.innerHTML = `<p class="px-2 py-6 text-center text-gray-500">Loading…</p>`
  }

  _renderBreadcrumb() {
    if (!this.hasBreadcrumbTarget) return
    if (this._stack.length === 0) {
      this.breadcrumbTarget.textContent = "Top folders"
      return
    }
    const parts = this._stack.map((c, i) => {
      const last = i === this._stack.length - 1
      if (last) {
        return `<span class="text-gray-700">${this._escape(c.label)}</span>`
      }
      return `<button type="button" class="text-brand hover:underline" data-action="click->acc-file-picker#_navigateToCrumb" data-crumb-index="${i}">${this._escape(c.label)}</button>`
    })
    this.breadcrumbTarget.innerHTML = parts.join('<span class="mx-1 text-gray-400">/</span>')
  }

  _renderContents(folders, files) {
    if (!this.hasContentTarget) return
    if (folders.length === 0 && files.length === 0) {
      this.contentTarget.innerHTML = `<p class="px-2 py-6 text-center text-gray-500">No folders or supported files here.</p>`
      return
    }

    const folderRows = folders.map((f) => `
      <button type="button"
              class="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200"
              data-action="click->acc-file-picker#_navigateFolder"
              data-folder-id="${this._escape(f.id)}"
              data-folder-name="${this._escape(f.name)}">
        <svg class="w-5 h-5 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M3.5 4A1.5 1.5 0 002 5.5v9A1.5 1.5 0 003.5 16h13a1.5 1.5 0 001.5-1.5V7A1.5 1.5 0 0016.5 5.5H10L8.5 4H3.5z"/></svg>
        <span class="text-[14px] text-gray-900">${this._escape(f.name)}</span>
      </button>`).join("")

    const fileRows = files.map((f) => {
      const sizeLabel = f.size_bytes ? this._humanSize(f.size_bytes) : ""
      return `
      <button type="button"
              class="w-full text-left flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 border border-transparent hover:border-gray-200"
              data-action="click->acc-file-picker#_pickFile"
              data-item='${this._jsonEscape(f)}'>
        <span class="inline-flex w-8 h-8 rounded bg-brand-50 text-brand items-center justify-center text-[10px] font-semibold flex-shrink-0">${this._escape((f.extension || "").toUpperCase())}</span>
        <span class="flex-1 min-w-0">
          <span class="block text-[14px] text-gray-900 truncate">${this._escape(f.name)}</span>
          <span class="block text-[12px] text-gray-500">${sizeLabel}</span>
        </span>
        <span class="text-[12px] text-brand font-medium">Add</span>
      </button>`
    }).join("")

    this.contentTarget.innerHTML = `
      <div class="space-y-1">
        ${folderRows}
        ${folderRows && fileRows ? '<div class="my-2 border-t border-gray-100"></div>' : ''}
        ${fileRows}
      </div>`
  }

  _navigateFolder(event) {
    const id   = event.currentTarget.dataset.folderId
    const name = event.currentTarget.dataset.folderName || "Folder"
    if (!id) return
    this._navigate(id, name)
  }

  _renderError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.remove("hidden")
    this.errorTarget.classList.add("text-rose-700")
    this.errorTarget.textContent = message
  }

  _clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.classList.add("hidden")
    this.errorTarget.textContent = ""
  }

  _humanSize(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return ""
    const units = ["B", "KB", "MB", "GB", "TB"]
    let value = bytes
    let i = 0
    while (value >= 1024 && i < units.length - 1) {
      value /= 1024
      i += 1
    }
    return `${value.toFixed(value < 10 && i > 0 ? 1 : 0)} ${units[i]}`
  }

  _escape(value) {
    return String(value ?? "").replace(/[&<>"']/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
    }[c]))
  }

  _jsonEscape(obj) {
    return this._escape(JSON.stringify(obj))
  }
}
